import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, BookOpen, Compass, CircleDollarSign, Check, X, RefreshCw, Eye, Trash2, MessageSquare, ShieldCheck, Plus, Search, Star, ImageIcon, Mail, PhoneCall } from 'lucide-react';



const compressImage = (file, callback) => {
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (event) => {
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      const MAX_WIDTH = 800;
      const MAX_HEIGHT = 800;
      if (width > height) {
        if (width > MAX_WIDTH) { height = Math.round((height * MAX_WIDTH) / width); width = MAX_WIDTH; }
      } else {
        if (height > MAX_HEIGHT) { width = Math.round((width * MAX_HEIGHT) / height); height = MAX_HEIGHT; }
      }
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      callback(canvas.toDataURL('image/jpeg', 0.7));
    };
    img.src = event.target.result;
  };
  reader.readAsDataURL(file);
};

export default function Admin() {
  const navigate = useNavigate();
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('admin_authenticated') === 'true';
  });
  const [currentAdminUser, setCurrentAdminUser] = useState(() => {
    return localStorage.getItem('admin_email') || '';
  });

  // Tab & Data States
  const [activeTab, setActiveTab] = useState('bookings'); // bookings | journeys | chats
  const [stats, setStats] = useState({
    total_bookings: 0,
    total_revenue: 0,
    total_journeys: 0,
    total_chats: 0,
    total_stories: 0,
    total_users: 0
  });

  const [bookings, setBookings] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [stories, setStories] = useState([]);
  const [users, setUsers] = useState([]);
  const [retreats, setRetreats] = useState([]);
  const [sanctuaries, setSanctuaries] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [expertCalls, setExpertCalls] = useState([]);
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [showAddExperienceModal, setShowAddExperienceModal] = useState(false);
  const [newExperience, setNewExperience] = useState({

    title: '',
    philosophy: '',
    philosophyTranslation: '',
    icon: 'Compass',
    image: '',
    description: '',
    teachings: '',
    centers: '',
    duration: ''
  });

  // Detailed Modal Views
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedChatSession, setSelectedChatSession] = useState(null);
  const [selectedChatHistory, setSelectedChatHistory] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);


  // Filters & Search
  const [bookingSearch, setBookingSearch] = useState('');
  const [bookingStatusFilter, setBookingStatusFilter] = useState('All');

  // Loading & Toast Notifications
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Add Journey Modal States & Handlers
  const isTimeReached = (date, time) => {
    try {
      const callDateTime = new Date(`${date}T${time}`);
      return new Date() >= callDateTime;
    } catch(e) { return true; }
  };

  const [showAddJourneyModal, setShowAddJourneyModal] = useState(false);
  const [newJourney, setNewJourney] = useState({
    title: '',
    summary: '',
    focus: 'Yoga',
    comfort: 'ashram',
    region: 'North India'
  });
  const [days, setDays] = useState([{ title: '', detail: '' }]);

  const handleAddDay = () => {
    setDays(prev => [...prev, { title: '', detail: '' }]);
  };

  const handleRemoveDay = (index) => {
    if (days.length === 1) return;
    setDays(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleDayChange = (index, field, value) => {
    setDays(prev => prev.map((day, idx) => idx === index ? { ...day, [field]: value } : day));
  };

  const handleCreateJourneySubmit = async (e) => {
    e.preventDefault();
    if (!newJourney.title.trim() || !newJourney.summary.trim()) {
      showToast('Please fill out Title and Summary fields.', 'error');
      return;
    }

    const hasEmptyDetails = days.some(day => !day.detail.trim());
    if (hasEmptyDetails) {
      showToast('Please fill out the details for all days in the itinerary.', 'error');
      return;
    }

    try {
      const payload = {
        title: newJourney.title,
        summary: newJourney.summary,
        focus: newJourney.focus,
        duration: days.length,
        comfort: newJourney.comfort,
        region: newJourney.region,
        days: days.map((day, idx) => ({
          dayNum: idx + 1,
          title: day.title || `Day ${idx + 1}`,
          detail: day.detail
        }))
      };

      const res = await fetch(`${apiBase}/api/journeys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.status === 429) { 


        showToast('You are doing that too fast! Please wait a minute before trying again.', 'error');


        return;


      }


      if (res.ok) {
        showToast('New spiritual journey added successfully!');
        setShowAddJourneyModal(false);
        setNewJourney({
          title: '',
          summary: '',
          focus: 'Yoga',
          comfort: 'ashram',
          region: 'North India'
        });
        setDays([{ title: '', detail: '' }]);
        fetchData();
      } else {
        const errorData = await res.json();
        showToast(errorData.detail || 'Failed to create journey.', 'error');
      }
    } catch (err) {
      showToast('Connection error. Server may be offline.', 'error');
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentAdminUser('');
    localStorage.removeItem('admin_authenticated');
    localStorage.removeItem('admin_email');
    showToast('Logged out successfully.', 'info');
    setTimeout(() => {
      navigate('/login');
    }, 1000);
  };

  // Add Retreat Modal States & Handlers
  const [showAddRetreatModal, setShowAddRetreatModal] = useState(false);
  const [newRetreat, setNewRetreat] = useState({

    name: '',

    desc: '',
    image: '',
    fullDesc: '',
    highlights: ''
  });

  const handleRetreatImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    compressImage(file, (dataUrl) => {
      setNewRetreat(prev => ({ ...prev, image: dataUrl }));
    });
  };

  useEffect(() => {
    const isAnyModalOpen = !!(selectedBooking || selectedStory || showAddJourneyModal || showAddRetreatModal);
    if (isAnyModalOpen) {
      document.body.style.overflow = 'hidden';
      if (window.lenis) window.lenis.stop();
    } else {
      document.body.style.overflow = 'auto';
      if (window.lenis) window.lenis.start();
    }
    return () => {
      document.body.style.overflow = 'auto';
      if (window.lenis) window.lenis.start();
    };
  }, [selectedBooking, selectedStory, showAddJourneyModal, showAddRetreatModal]);

  const handleCreateRetreatSubmit = async (e) => {
    e.preventDefault();
    const missing = [];
    if (!newRetreat.name) missing.push('Name');
    if (!newRetreat.desc) missing.push('Short Description');
    if (!newRetreat.fullDesc) missing.push('Full Description');
    if (!newRetreat.highlights) missing.push('Highlights');
    if (missing.length > 0) {
      showToast(`Please fill: ${missing.join(', ')}`, 'error');
      return;
    }

    try {
      const payload = {
        ...newRetreat,

        highlights: newRetreat.highlights.split('\n').filter(h => h.trim())
      };

      const res = await fetch(`${apiBase}/api/retreats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.status === 429) { 

        showToast('You are doing that too fast! Please wait a minute before trying again.', 'error');

        return;

      }

      if (res.ok) {
        showToast('Retreat added successfully!');
        setShowAddRetreatModal(false);
        setNewRetreat({ name: '', desc: '', image: '', fullDesc: '', highlights: '' });
        fetchData();
      } else {
        const errorData = await res.json();
        showToast(`Failed to add retreat: ${errorData.detail || 'Unknown error'}`, 'error');
      }
    } catch (err) {
      showToast('Error connecting to server: ' + err.message, 'error');
    }
  };

  const handleDeleteRetreat = async (retreatId) => {
    if (!window.confirm('Are you sure you want to delete this retreat?')) return;
    try {
      const res = await fetch(`${apiBase}/api/retreats/${retreatId}`, {
        method: 'DELETE'
      });
      if (res.status === 429) { 

        showToast('You are doing that too fast! Please wait a minute before trying again.', 'error');

        return;

      }

      if (res.ok || res.status === 204) {
        showToast('Retreat deleted successfully.');
        fetchData();
      } else {
        showToast('Failed to delete retreat.', 'error');
      }
    } catch (err) {
      showToast('Error deleting retreat.', 'error');
    }
  };

  // Add Sanctuary Modal States & Handlers
  const [showAddSanctuaryModal, setShowAddSanctuaryModal] = useState(false);
  const [newSanctuary, setNewSanctuary] = useState({

    title: '',
    state: '',
    tagline: '',
    image: '',
    description: '',
    spiritualEssence: '',
    activities: '',
    bestTime: '',
    signatureExperience: ''
  });

  const handleSanctuaryImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      compressImage(file, (dataUrl) => {
        setNewSanctuary(prev => ({ ...prev, image: dataUrl }));
      });
    }
  };

  const handleCreateSanctuarySubmit = async (e) => {
    e.preventDefault();
    const missing = [];
    if (!newSanctuary.title) missing.push('Title');
    if (!newSanctuary.state) missing.push('State');
    if (!newSanctuary.tagline) missing.push('Tagline');
    if (!newSanctuary.description) missing.push('Description');
    if (!newSanctuary.spiritualEssence) missing.push('Spiritual Essence');
    if (!newSanctuary.activities) missing.push('Activities');
    if (!newSanctuary.bestTime) missing.push('Best Time');
    if (!newSanctuary.signatureExperience) missing.push('Signature Experience');
    if (missing.length > 0) {
      showToast(`Please fill: ${missing.join(', ')}`, 'error');
      return;
    }

    try {
      const payload = {
        ...newSanctuary,
        activities: newSanctuary.activities.split('\n').filter(a => a.trim())
      };

      const res = await fetch(`${apiBase}/api/sanctuaries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.status === 429) { 

        showToast('You are doing that too fast! Please wait a minute before trying again.', 'error');

        return;

      }

      if (res.ok) {
        showToast('Sanctuary added successfully!');
        setShowAddSanctuaryModal(false);
        setNewSanctuary({ title: '', state: '', tagline: '', image: '', description: '', spiritualEssence: '', activities: '', bestTime: '', signatureExperience: '' });
        fetchData();
      } else {
        const errorData = await res.json();
        let errorMsg = 'Unknown error';
        if (errorData.detail && Array.isArray(errorData.detail)) {
          errorMsg = errorData.detail.map(e => `${e.loc.slice(-1)[0]}: ${e.msg}`).join(', ');
        } else if (errorData.detail) {
          errorMsg = errorData.detail;
        }
        showToast(`Failed to add sanctuary: ${errorMsg}`, 'error');
      }
    } catch (err) {
      showToast('Error connecting to server: ' + err.message, 'error');
    }
  };

  const handleDeleteSanctuary = async (sanctuaryId) => {
    if (!window.confirm('Are you sure you want to delete this sanctuary?')) return;
    try {
      const res = await fetch(`${apiBase}/api/sanctuaries/${sanctuaryId}`, {
        method: 'DELETE'
      });
      if (res.status === 429) { 

        showToast('You are doing that too fast! Please wait a minute before trying again.', 'error');

        return;

      }

      if (res.ok || res.status === 204) {
        showToast('Sanctuary deleted successfully.');
        fetchData();
      } else {
        showToast('Failed to delete sanctuary.', 'error');
      }
    } catch (err) {
      showToast('Error deleting sanctuary.', 'error');
    }
  };

  const handleExperienceImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      compressImage(file, (dataUrl) => {
        setNewExperience(prev => ({ ...prev, image: dataUrl }));
      });
    }
  };

  const handleCreateExperienceSubmit = async (e) => {
    e.preventDefault();
    const missing = [];
    if (!newExperience.title) missing.push('Title');
    if (!newExperience.philosophy) missing.push('Philosophy');
    if (!newExperience.philosophyTranslation) missing.push('Translation');
    if (!newExperience.description) missing.push('Description');
    if (!newExperience.teachings) missing.push('Teachings');
    if (!newExperience.centers) missing.push('Centers');
    if (!newExperience.duration) missing.push('Duration');
    if (missing.length > 0) {
      showToast(`Please fill: ${missing.join(', ')}`, 'error');
      return;
    }

    try {
      const payload = {
        ...newExperience,
        teachings: newExperience.teachings.split('\n').filter(t => t.trim())
      };

      const res = await fetch(`${apiBase}/api/experiences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.status === 429) { 

        showToast('You are doing that too fast! Please wait a minute before trying again.', 'error');

        return;

      }

      if (res.ok) {
        showToast('Experience added successfully!');
        setShowAddExperienceModal(false);
        setNewExperience({ title: '', philosophy: '', philosophyTranslation: '', icon: 'Compass', image: '', description: '', teachings: '', centers: '', duration: '' });
        fetchData();
      } else {
        const errorData = await res.json();
        let errorMsg = 'Unknown error';
        if (errorData.detail && Array.isArray(errorData.detail)) {
          errorMsg = errorData.detail.map(e => `${e.loc.slice(-1)[0]}: ${e.msg}`).join(', ');
        } else if (errorData.detail) {
          errorMsg = errorData.detail;
        }
        showToast(`Failed to add experience: ${errorMsg}`, 'error');
      }
    } catch (err) {
      showToast('Error connecting to server: ' + err.message, 'error');
    }
  };

  const handleDeleteExperience = async (experienceId) => {
    if (!window.confirm('Are you sure you want to delete this experience?')) return;
    try {
      const res = await fetch(`${apiBase}/api/experiences/${experienceId}`, {
        method: 'DELETE'
      });
      if (res.status === 429) { 

        showToast('You are doing that too fast! Please wait a minute before trying again.', 'error');

        return;

      }

      if (res.ok || res.status === 204) {
        showToast('Experience deleted successfully.');
        fetchData();
      } else {
        showToast('Failed to delete experience.', 'error');
      }
    } catch (err) {
      showToast('Error deleting experience.', 'error');
    }
  };

  // Fetch all admin console data
  const fetchData = async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      // 1. Fetch Stats
      const statsRes = await fetch(`${apiBase}/api/admin/stats`);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // 2. Fetch Bookings
      const bookingsRes = await fetch(`${apiBase}/api/admin/bookings`);
      if (bookingsRes.ok) {
        const bookingsData = await bookingsRes.json();
        setBookings(bookingsData);
      }

      // 3. Fetch Journeys
      const journeysRes = await fetch(`${apiBase}/api/admin/journeys`);
      if (journeysRes.ok) {
        const journeysData = await journeysRes.json();
        setJourneys(journeysData);
      }

      // 4. Fetch Chat summaries
      const chatsRes = await fetch(`${apiBase}/api/admin/chats`);
      if (chatsRes.ok) {
        const chatsData = await chatsRes.json();
        setChatSessions(chatsData);
      }

      // 5. Fetch Stories
      const storiesRes = await fetch(`${apiBase}/api/admin/stories`);
      if (storiesRes.ok) {
        const storiesData = await storiesRes.json();
        setStories(storiesData);
      }

      // 6. Fetch Users
      const usersRes = await fetch(`${apiBase}/api/admin/users`);
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData);
      }

      // 7. Fetch Retreats
      const retreatsRes = await fetch(`${apiBase}/api/retreats`);
      if (retreatsRes.ok) {
        const retreatsData = await retreatsRes.json();
        setRetreats(retreatsData);
      }

      // 8. Fetch Sanctuaries
      const sanctuariesRes = await fetch(`${apiBase}/api/sanctuaries`);
      if (sanctuariesRes.ok) {
        const sanctuariesData = await sanctuariesRes.json();
        setSanctuaries(sanctuariesData);
      }

      // 9. Fetch Experiences
      const experiencesRes = await fetch(`${apiBase}/api/experiences`);
      if (experiencesRes.ok) {
        const experiencesData = await experiencesRes.json();
        setExperiences(experiencesData);
      }

      // 10. Fetch Subscribers
      const subscribersRes = await fetch(`${apiBase}/api/admin/subscribers`);
      if (subscribersRes.ok) {
        const subscribersData = await subscribersRes.json();
        setSubscribers(subscribersData);
      }

      // 11. Fetch Expert Calls
      const expertCallsRes = await fetch(`${apiBase}/api/admin/expert-calls`);
      if (expertCallsRes.ok) {
        const expertCallsData = await expertCallsRes.json();
        setExpertCalls(expertCallsData);
      }

      // 12. Fetch Scheduled Calls
      const scheduledCallsRes = await fetch(`${apiBase}/api/admin/scheduled-calls`);
      if (scheduledCallsRes.ok) {
        const scheduledCallsData = await scheduledCallsRes.json();
        setScheduledCalls(scheduledCallsData);
      }

    } catch (error) {
      console.error('Error fetching admin data:', error);
      showToast('Database connection failed.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Join Expert Call
  const handleJoinExpertCall = async (roomName) => {
    try {
      const res = await fetch(`${apiBase}/api/admin/meet/jwt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room_name: roomName })
      });
      const data = await res.json();
      if (data.url) {
        window.open(data.url, '_blank');
      } else {
        showToast('Could not join the meeting.', 'error');
      }
    } catch (err) {
      showToast('Network error trying to join the call.', 'error');
    }
  };

  const handleDeleteScheduledCall = async (callId) => {
    if (!window.confirm('Are you sure you want to delete this scheduled call?')) return;
    try {
      const res = await fetch(`${apiBase}/api/admin/scheduled-calls/${callId}`, { method: 'DELETE' });
      if (res.status === 429) { 

        showToast('You are doing that too fast! Please wait a minute before trying again.', 'error');

        return;

      }

      if (res.ok) {
        setScheduledCalls(prev => prev.filter(c => c.id !== callId));
        showToast('Call deleted successfully.');
      } else {
        showToast('Failed to delete call.', 'error');
      }
    } catch (err) {
      showToast('Error deleting call.', 'error');
    }
  };

  const handleUpdateExpertCallStatus = async (callId, newStatus) => {
    try {
      const res = await fetch(`${apiBase}/api/admin/expert-calls/${callId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.status === 429) { 

        showToast('You are doing that too fast! Please wait a minute before trying again.', 'error');

        return;

      }

      if (res.ok) {
        showToast('Call status updated.');
        fetchData();
      } else {
        showToast('Failed to update status.', 'error');
      }
    } catch (err) {
      showToast('Error updating status.', 'error');
    }
  };

  // Update Booking Status
  const handleUpdateStatus = async (bookingId, newStatus) => {
    try {
      const res = await fetch(`${apiBase}/api/admin/bookings/${bookingId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.status === 429) { 

        showToast('You are doing that too fast! Please wait a minute before trying again.', 'error');

        return;

      }

      if (res.ok) {
        showToast(`Booking status updated to ${newStatus}.`);
        fetchData();
        if (selectedBooking && selectedBooking.id === bookingId) {
          setSelectedBooking(prev => ({ ...prev, status: newStatus }));
        }
      } else {
        showToast('Failed to update status.', 'error');
      }
    } catch (err) {
      showToast('Error updating status.', 'error');
    }
  };

  // Delete Booking
  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to permanently delete this booking registration?')) return;
    try {
      const res = await fetch(`${apiBase}/api/admin/bookings/${bookingId}`, {
        method: 'DELETE'
      });
      if (res.status === 204 || res.ok) {
        showToast('Booking deleted successfully.');
        setSelectedBooking(null);
        fetchData();
      } else {
        showToast('Failed to delete booking.', 'error');
      }
    } catch (err) {
      showToast('Error deleting booking.', 'error');
    }
  };

  // Delete Saved Journey
  const handleDeleteJourney = async (journeyId) => {
    if (!window.confirm('Are you sure you want to delete this custom planned itinerary?')) return;
    try {
      const res = await fetch(`${apiBase}/api/admin/journeys/${journeyId}`, {
        method: 'DELETE'
      });
      if (res.status === 204 || res.ok) {
        showToast('Itinerary deleted successfully.');
        fetchData();
      } else {
        showToast('Failed to delete itinerary.', 'error');
      }
    } catch (err) {
      showToast('Error deleting itinerary.', 'error');
    }
  };

  // Inspect Chat Transcript
  const handleInspectChat = async (sessionId) => {
    try {
      const res = await fetch(`${apiBase}/api/ai/chat/${sessionId}`);
      if (res.status === 429) { 

        showToast('You are doing that too fast! Please wait a minute before trying again.', 'error');

        return;

      }

      if (res.ok) {
        const history = await res.json();
        setSelectedChatHistory(history);
        setSelectedChatSession(sessionId);
      } else {
        showToast('Failed to retrieve chat transcript.', 'error');
      }
    } catch (err) {
      showToast('Error loading chat session.', 'error');
    }
  };

  // Delete Chat Session
  const handleDeleteChatSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this AI guide chat history session?')) return;
    try {
      const res = await fetch(`${apiBase}/api/admin/chats/${sessionId}`, {
        method: 'DELETE'
      });
      if (res.status === 204 || res.ok) {
        showToast('Chat history cleared.');
        if (selectedChatSession === sessionId) {
          setSelectedChatSession(null);
          setSelectedChatHistory([]);
        }
        fetchData();
      } else {
        showToast('Failed to clear chat.', 'error');
      }
    } catch (err) {
      showToast('Error deleting chat session.', 'error');
    }
  };

  const handleDeleteStory = async (storyId) => {
    if (!window.confirm('Are you sure you want to permanently delete this seeker story?')) return;
    try {
      const res = await fetch(`${apiBase}/api/admin/stories/${storyId}`, {
        method: 'DELETE'
      });
      if (res.status === 204 || res.ok) {
        showToast('Story deleted successfully.');
        setSelectedStory(null);
        fetchData();
      } else {
        showToast('Failed to delete story.', 'error');
      }
    } catch (err) {
      showToast('Error deleting story.', 'error');
    }
  };

  const handleApproveStory = async (storyId) => {
    try {
      const res = await fetch(`${apiBase}/api/admin/stories/${storyId}/approve`, {
        method: 'PATCH'
      });
      if (res.status === 429) { 

        showToast('You are doing that too fast! Please wait a minute before trying again.', 'error');

        return;

      }

      if (res.ok) {
        showToast('Story approved successfully.');
        fetchData();
        if (selectedStory && selectedStory.id === storyId) {
          setSelectedStory(prev => ({ ...prev, approved: true }));
        }
      } else {
        showToast('Failed to approve story.', 'error');
      }
    } catch (err) {
      showToast('Error approving story.', 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to permanently delete this registered user? They will no longer be able to log in.')) return;
    try {
      const res = await fetch(`${apiBase}/api/admin/users/${userId}`, {
        method: 'DELETE'
      });
      if (res.status === 204 || res.ok) {
        showToast('User account deleted successfully.');
        fetchData();
      } else {
        showToast('Failed to delete user.', 'error');
      }
    } catch (err) {
      showToast('Error deleting user.', 'error');
    }
  };


  // Filtering
  const filteredBookings = bookings.filter(b => {
    const matchesSearch = b.name.toLowerCase().includes(bookingSearch.toLowerCase()) ||
      b.email.toLowerCase().includes(bookingSearch.toLowerCase()) ||
      b.booking_ref.toLowerCase().includes(bookingSearch.toLowerCase());
    const matchesStatus = bookingStatusFilter === 'All' || b.status === bookingStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const getPackageNameText = (pkgKey) => {
    const mapping = {
      yoga: 'Himalayan Hatha & Yoga',
      ayurveda: 'Ayurvedic Detox & Kerala',
      silent: 'Vipassana Silent Meditation',
      temple: 'Temple Geometry Pilgrimage'
    };
    return mapping[pkgKey] || pkgKey;
  };

  const getComfortTierText = (comfortKey) => {
    const mapping = {
      ashram: 'Ashram Simplicity',
      heritage: 'Heritage Comfort',
      royal: 'Royal Luxury (Palace)'
    };
    return mapping[comfortKey] || comfortKey;
  };

  if (!isAuthenticated) {
    return (
      <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
        <p>Redirecting to login portal...</p>
      </div>
    );
  }

  // RENDER: LOGGED IN ADMIN CONSOLE
  return (
    <div className="admin-page" style={{ padding: '60px 0', minHeight: '100vh', position: 'relative', backgroundColor: 'var(--bg-deep)' }}>

      {/* Toast Alert */}
      {toast && (
        <div
          className="animate-fade-in admin-toast"
          style={{
            position: 'fixed',
            top: '90px',
            right: '30px',
            zIndex: 999999,
            backgroundColor: toast.type === 'error' ? 'var(--accent-terracotta)' : 'var(--accent-green)',
            color: '#fff',
            padding: '14px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '0.85rem',
            fontWeight: '600'
          }}
        >
          {toast.type === 'error' ? <X size={18} /> : <Check size={18} />}
          <span>{toast.message}</span>
        </div>
      )}

      <div style={{ width: '90%', maxWidth: '1200px', margin: '0 auto' }}>

        {/* Dashboard Title Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '35px', borderBottom: '1px solid rgba(181, 137, 37, 0.15)', paddingBottom: '20px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ShieldCheck className="text-gold" size={20} />
              <span style={{ textTransform: 'uppercase', letterSpacing: '0.2em', fontSize: '0.75rem', fontWeight: '700', color: 'var(--primary-gold)' }}>
                Authenticated Admin: {currentAdminUser}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '5px' }}>
              <img src="/images/new-logo.png" alt="Mystic Routes Logo" style={{ height: '45px', objectFit: 'contain' }} />
              <h1 className="font-serif" style={{ fontSize: '2.5rem', color: 'var(--text-light)', margin: 0 }}>
                Dashboard
              </h1>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => window.location.reload()}
              className="btn-outline"
              style={{ padding: '8px 16px', borderRadius: '20px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '6px' }}
              title="Reload Page"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={handleLogout}
              className="btn-outline"
              style={{ padding: '8px 16px', borderRadius: '20px', fontSize: '0.75rem', borderColor: 'var(--accent-terracotta)', color: 'var(--accent-terracotta)' }}
            >
              Disconnect
            </button>
          </div>
        </div>

        {/* 4 KPI Metric Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '40px' }}>
          <div className="glass-card" style={{ padding: '20px 25px', display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ color: 'var(--primary-saffron)', background: 'var(--primary-saffron-trans)', padding: '12px', borderRadius: '50%' }}>
              <BookOpen size={24} />
            </div>
            <div>
              <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Total Bookings</span>
              <span className="font-serif" style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--text-light)' }}>{stats.total_bookings}</span>
            </div>
          </div>

          <div className="glass-card" style={{ padding: '20px 25px', display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ color: 'var(--primary-gold)', background: 'var(--primary-gold-trans)', padding: '12px', borderRadius: '50%' }}>
              <CircleDollarSign size={24} />
            </div>
            <div>
              <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Total Revenue</span>
              <span className="font-serif" style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--text-light)' }}>${stats.total_revenue}</span>
            </div>
          </div>

          <div className="glass-card" style={{ padding: '20px 25px', display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ color: 'var(--primary-gold)', background: 'var(--primary-gold-trans)', padding: '12px', borderRadius: '50%' }}>
              <Compass size={24} />
            </div>
            <div>
              <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Planned Journeys</span>
              <span className="font-serif" style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--text-light)' }}>{stats.total_journeys}</span>
            </div>
          </div>

          <div className="glass-card" style={{ padding: '20px 25px', display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ color: 'var(--primary-saffron)', background: 'var(--primary-saffron-trans)', padding: '12px', borderRadius: '50%' }}>
              <MessageSquare size={24} />
            </div>
            <div>
              <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>AI Chat Logs</span>
              <span className="font-serif" style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--text-light)' }}>{stats.total_chats}</span>
            </div>
          </div>

          <div className="glass-card" style={{ padding: '20px 25px', display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ color: 'var(--primary-gold)', background: 'var(--primary-gold-trans)', padding: '12px', borderRadius: '50%' }}>
              <BookOpen size={24} />
            </div>
            <div>
              <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Shared Stories</span>
              <span className="font-serif" style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--text-light)' }}>{stats.total_stories || 0}</span>
            </div>
          </div>

          <div className="glass-card" style={{ padding: '20px 25px', display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ color: 'var(--primary-saffron)', background: 'var(--primary-saffron-trans)', padding: '12px', borderRadius: '50%' }}>
              <ShieldCheck size={24} />
            </div>
            <div>
              <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Registered Seekers</span>
              <span className="font-serif" style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--text-light)' }}>{stats.total_users || 0}</span>
            </div>
          </div>

          <div className="glass-card" style={{ padding: '20px 25px', display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ color: 'var(--primary-gold)', background: 'var(--primary-gold-trans)', padding: '12px', borderRadius: '50%' }}>
              <Compass size={24} />
            </div>
            <div>
              <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Total Experiences</span>
              <span className="font-serif" style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--text-light)' }}>{stats.total_experiences || 0}</span>
            </div>
          </div>

        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid rgba(181, 137, 37, 0.15)', marginBottom: '30px', gap: '10px' }}>
          <button
            onClick={() => setActiveTab('bookings')}
            className="font-serif"
            style={{
              padding: '12px 24px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'bookings' ? '2px solid var(--primary-gold)' : '2px solid transparent',
              color: activeTab === 'bookings' ? 'var(--primary-gold)' : 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1.05rem',
              fontWeight: activeTab === 'bookings' ? '600' : '400',
              transition: 'var(--transition-fast)'
            }}
          >
            Retreats
          </button>
          <button
            onClick={() => setActiveTab('expert_calls')}
            className="font-serif"
            style={{
              padding: '12px 24px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'expert_calls' ? '2px solid var(--primary-gold)' : '2px solid transparent',
              color: activeTab === 'expert_calls' ? 'var(--primary-gold)' : 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1.05rem',
              fontWeight: activeTab === 'expert_calls' ? '600' : '400',
              transition: 'var(--transition-fast)'
            }}
          >
            Expert Calls
          </button>
          <button
            onClick={() => setActiveTab('journeys')}
            className="font-serif"
            style={{
              padding: '12px 24px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'journeys' ? '2px solid var(--primary-gold)' : '2px solid transparent',
              color: activeTab === 'journeys' ? 'var(--primary-gold)' : 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1.05rem',
              fontWeight: activeTab === 'journeys' ? '600' : '400',
              transition: 'var(--transition-fast)'
            }}
          >
            Journey Planner
          </button>
          <button
            onClick={() => setActiveTab('chats')}
            className="font-serif"
            style={{
              padding: '12px 24px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'chats' ? '2px solid var(--primary-gold)' : '2px solid transparent',
              color: activeTab === 'chats' ? 'var(--primary-gold)' : 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1.05rem',
              fontWeight: activeTab === 'chats' ? '600' : '400',
              transition: 'var(--transition-fast)'
            }}
          >
            Darshan
          </button>

          <button
            onClick={() => setActiveTab('stories')}
            className="font-serif"
            style={{
              padding: '12px 24px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'stories' ? '2px solid var(--primary-gold)' : '2px solid transparent',
              color: activeTab === 'stories' ? 'var(--primary-gold)' : 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1.05rem',
              fontWeight: activeTab === 'stories' ? '600' : '400',
              transition: 'var(--transition-fast)'
            }}
          >
            Stories
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className="font-serif"
            style={{
              padding: '12px 24px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'users' ? '2px solid var(--primary-gold)' : '2px solid transparent',
              color: activeTab === 'users' ? 'var(--primary-gold)' : 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1.05rem',
              fontWeight: activeTab === 'users' ? '600' : '400',
              transition: 'var(--transition-fast)'
            }}
          >
            Registered Users
          </button>
          <button
            onClick={() => setActiveTab('sanctuaries')}
            className="font-serif"
            style={{
              padding: '12px 24px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'sanctuaries' ? '2px solid var(--primary-gold)' : '2px solid transparent',
              color: activeTab === 'sanctuaries' ? 'var(--primary-gold)' : 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1.05rem',
              fontWeight: activeTab === 'sanctuaries' ? '600' : '400',
              transition: 'var(--transition-fast)'
            }}
          >
            Sanctuaries
          </button>
          <button
            onClick={() => setActiveTab('experiences')}
            className="font-serif"
            style={{
              padding: '12px 24px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'experiences' ? '2px solid var(--primary-gold)' : '2px solid transparent',
              color: activeTab === 'experiences' ? 'var(--primary-gold)' : 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1.05rem',
              fontWeight: activeTab === 'experiences' ? '600' : '400',
              transition: 'var(--transition-fast)'
            }}
          >
            Experiences
          </button>
          <button
            onClick={() => setActiveTab('subscribers')}
            className="font-serif"
            style={{
              padding: '12px 24px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'subscribers' ? '2px solid var(--primary-gold)' : '2px solid transparent',
              color: activeTab === 'subscribers' ? 'var(--primary-gold)' : 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1.05rem',
              fontWeight: activeTab === 'subscribers' ? '600' : '400',
              transition: 'var(--transition-fast)'
            }}
          >
            Subscribers
          </button>
        </div>


        {/* Tab Content Rendering */}
        <div style={{ minHeight: '400px' }}>

          {/* TAB 1: RETREATS & BOOKINGS */}
          {activeTab === 'bookings' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
              <div className="glass-card animate-fade-in" style={{ padding: '30px' }}>
                <h3 className="font-serif text-gold" style={{ fontSize: '1.4rem', marginBottom: '20px' }}>Retreat Bookings</h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px', marginBottom: '25px' }}>
                  <div style={{ position: 'relative', width: '100%', maxWidth: '350px' }}>
                    <Search size={16} style={{ position: 'absolute', left: '15px', top: '12px', color: 'var(--text-muted)' }} />
                    <input
                      type="text"
                      placeholder="Search by seeker name, email, or ref..."
                      value={bookingSearch}
                      onChange={(e) => setBookingSearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 15px 10px 42px',
                        borderRadius: '20px',
                        border: '1px solid rgba(181,137,37,0.2)',
                        background: 'rgba(255,255,255,0.7)',
                        fontSize: '0.85rem',
                        outline: 'none',
                        color: 'var(--text-light)'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Status Filter:</span>
                    <select
                      value={bookingStatusFilter}
                      onChange={(e) => setBookingStatusFilter(e.target.value)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '20px',
                        border: '1px solid rgba(181,137,37,0.2)',
                        background: 'rgba(255,255,255,0.7)',
                        fontSize: '0.85rem',
                        color: 'var(--text-light)',
                        outline: 'none'
                      }}
                    >
                      <option value="All">All Statuses</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                {filteredBookings.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '50px 0', color: 'var(--text-muted)' }}>
                    <BookOpen size={48} style={{ margin: '0 auto 15px', opacity: 0.3 }} />
                    <p>No retreat bookings matching criteria found.</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.15)', color: 'var(--text-muted)' }}>
                          <th style={{ padding: '12px 10px' }}>Ref</th>
                          <th style={{ padding: '12px 10px' }}>Seeker</th>
                          <th style={{ padding: '12px 10px' }}>Package</th>
                          <th style={{ padding: '12px 10px' }}>Date</th>
                          <th style={{ padding: '12px 10px' }}>Price</th>
                          <th style={{ padding: '12px 10px' }}>Status</th>
                          <th style={{ padding: '12px 10px', textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBookings.map((b) => (
                          <tr key={b.id} style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.05)', color: 'var(--text-light)' }}>
                            <td style={{ padding: '14px 10px', fontWeight: 'bold' }}>{b.booking_ref}</td>
                            <td style={{ padding: '14px 10px' }}>
                              <div style={{ fontWeight: '500' }}>{b.name}</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{b.email}</div>
                              {b.phone && <div style={{ fontSize: '0.75rem', color: 'var(--primary-gold)' }}>{b.phone}</div>}
                            </td>
                            <td style={{ padding: '14px 10px' }}>
                              <div>{getPackageNameText(b.package_name)}</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{getComfortTierText(b.comfort)} ({b.duration} days)</div>
                            </td>
                            <td style={{ padding: '14px 10px' }}>{b.start_date}</td>
                            <td style={{ padding: '14px 10px', fontWeight: 'bold' }}>${b.price}</td>
                            <td style={{ padding: '14px 10px' }}>
                              <span
                                style={{
                                  padding: '4px 10px',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  backgroundColor: b.status === 'Confirmed' ? 'rgba(181, 137, 37, 0.1)' :
                                    b.status === 'Completed' ? 'rgba(46, 125, 50, 0.1)' : 'rgba(198, 40, 40, 0.1)',
                                  color: b.status === 'Confirmed' ? 'var(--primary-gold)' :
                                    b.status === 'Completed' ? 'var(--accent-green)' : 'var(--accent-terracotta)'
                                }}
                              >
                                {b.status}
                              </span>
                            </td>
                            <td style={{ padding: '14px 10px', textAlign: 'right' }}>
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                <button
                                  onClick={() => setSelectedBooking(b)}
                                  className="btn-outline"
                                  style={{ padding: '4px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center' }}
                                  title="View Details"
                                >
                                  <Eye size={14} />
                                </button>
                                <select
                                  value={b.status}
                                  onChange={(e) => handleUpdateStatus(b.id, e.target.value)}
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid rgba(181,137,37,0.2)',
                                    background: '#fff',
                                    fontSize: '0.8rem',
                                    color: 'var(--text-light)',
                                    outline: 'none'
                                  }}
                                >
                                  <option value="Confirmed">Confirmed</option>
                                  <option value="Completed">Completed</option>
                                  <option value="Cancelled">Cancelled</option>
                                </select>
                                <button
                                  onClick={() => handleDeleteBooking(b.id)}
                                  className="btn-outline"
                                  style={{ padding: '4px 8px', borderRadius: '4px', borderColor: 'var(--accent-terracotta)', color: 'var(--accent-terracotta)' }}
                                  title="Delete"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="glass-card animate-fade-in" style={{ padding: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                  <h3 className="font-serif text-gold" style={{ fontSize: '1.4rem' }}>Manage Retreats</h3>
                  <button
                    onClick={() => setShowAddRetreatModal(true)}
                    className="btn-gold"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '20px', fontSize: '0.85rem' }}
                  >
                    <Plus size={16} /> Add Retreat
                  </button>
                </div>

                {retreats.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '50px 0', color: 'var(--text-muted)' }}>
                    <Compass size={48} style={{ margin: '0 auto 15px', opacity: 0.3 }} />
                    <p>No retreats found in the database.</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.15)', color: 'var(--text-muted)' }}>
                          <th style={{ padding: '12px 10px' }}>ID</th>

                          <th style={{ padding: '12px 10px' }}>Name</th>

                          <th style={{ padding: '12px 10px' }}>Date Added</th>
                          <th style={{ padding: '12px 10px', textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {retreats.map((r) => (
                          <tr key={r.id} style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.05)', color: 'var(--text-light)' }}>
                            <td style={{ padding: '14px 10px', fontWeight: 'bold' }}>{r.id}</td>

                            <td style={{ padding: '14px 10px', fontWeight: '500' }}>{r.name}</td>

                            <td style={{ padding: '14px 10px' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                            <td style={{ padding: '14px 10px', textAlign: 'right' }}>
                              <button
                                onClick={() => handleDeleteRetreat(r.id)}
                                className="btn-outline"
                                style={{ padding: '4px 8px', borderRadius: '4px', borderColor: 'var(--accent-terracotta)', color: 'var(--accent-terracotta)' }}
                                title="Delete Retreat"
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'expert_calls' && (
            <div className="glass-card animate-fade-in" style={{ padding: '30px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                  <h3 className="font-serif" style={{ fontSize: '1.4rem', color: 'var(--text-light)', margin: 0 }}>Scheduled Calls</h3>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '3px 0 0 0' }}>Call requests waiting for your attention</p>
                </div>
              </div>

              {scheduledCalls.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '50px 0', color: 'var(--text-muted)' }}>
                  <Check size={48} style={{ margin: '0 auto 15px', opacity: 0.3 }} />
                  <p>No scheduled calls found.</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.15)', color: 'var(--text-muted)' }}>
                        <th style={{ padding: '12px 10px' }}>User</th>
                        <th style={{ padding: '12px 10px' }}>Email</th>
                        <th style={{ padding: '12px 10px' }}>Date</th>
                        <th style={{ padding: '12px 10px' }}>Time</th>
                        <th style={{ padding: '12px 10px' }}>Query</th>
                        <th style={{ padding: '12px 10px' }}>Submitted On</th>
                        <th style={{ padding: '12px 10px', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scheduledCalls.map((sc) => (
                        <tr key={sc.id} style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.05)', color: 'var(--text-light)' }}>
                          <td style={{ padding: '14px 10px', fontWeight: '500' }}>{sc.user_name}</td>
                          <td style={{ padding: '14px 10px' }}>{sc.user_email}</td>
                          <td style={{ padding: '14px 10px', color: 'var(--primary-gold)' }}>{sc.date}</td>
                          <td style={{ padding: '14px 10px', color: 'var(--primary-gold)' }}>{sc.time}</td>
                          <td style={{ padding: '14px 10px' }}>{sc.query}</td>
                          <td style={{ padding: '14px 10px' }}>{new Date(sc.created_at).toLocaleString()}</td>
                          <td style={{ padding: '14px 10px', textAlign: 'right' }}>
                            {isTimeReached(sc.date, sc.time) && (
                              <button
                                onClick={() => handleJoinExpertCall(`scheduled-${sc.id}`)}
                                className="btn-gold"
                                style={{ padding: '4px 12px', borderRadius: '4px', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}
                              >
                                Join Call
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteScheduledCall(sc.id)}
                              style={{
                                background: 'none',
                                border: '1px solid rgba(231, 76, 60, 0.5)',
                                color: '#e74c3c',
                                padding: '4px 12px',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(231, 76, 60, 0.1)'; }}
                              onMouseOut={(e) => { e.currentTarget.style.background = 'none'; }}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'journeys' && (
            <div className="glass-card animate-fade-in" style={{ padding: '30px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                  <h3 className="font-serif" style={{ fontSize: '1.4rem', color: 'var(--text-light)', margin: 0 }}>Custom Journey Requests</h3>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '3px 0 0 0' }}>Manage bespoke pilgrimage inquiries</p>
                </div>
              </div>

              {journeys.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '50px 0', color: 'var(--text-muted)' }}>
                  <Compass size={48} style={{ margin: '0 auto 15px', opacity: 0.3 }} />
                  <p>No custom journey requests found.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {journeys.map((j) => (
                    <div
                      key={j.id}
                      style={{
                        border: '1px solid rgba(181, 137, 37, 0.15)',
                        borderRadius: '12px',
                        padding: '20px',
                        backgroundColor: 'rgba(255, 255, 255, 0.3)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px', borderBottom: '1px solid rgba(181,137,37,0.08)', paddingBottom: '12px', marginBottom: '15px' }}>
                        <div>
                          <h3 className="font-serif" style={{ fontSize: '1.25rem', color: 'var(--text-light)', margin: '0 0 5px 0' }}>{j.place}</h3>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                            <span>Seeker: <strong style={{ color: 'var(--primary-gold)' }}>{j.name}</strong></span>
                            <span>Email: <strong>{j.email}</strong></span>
                            <span>Phone: <strong>{j.phone}</strong></span>
                            <span>Duration: <strong>{j.days} Days</strong></span>
                            <span>Submitted: <strong>{new Date(j.created_at).toLocaleDateString()}</strong></span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteJourney(j.id)}
                          className="btn-outline"
                          style={{ padding: '6px 12px', borderRadius: '4px', borderColor: 'var(--accent-terracotta)', color: 'var(--accent-terracotta)', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <Trash2 size={12} /> Delete Request
                        </button>
                      </div>

                      <div style={{ fontSize: '0.9rem', color: 'var(--text-light)', lineHeight: '1.5' }}>
                        <strong style={{ color: 'var(--primary-gold)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Journey Intentions & Details:</strong>
                        <p style={{ marginTop: '5px', whiteSpace: 'pre-wrap' }}>{j.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: AI CHATS */}
          {activeTab === 'chats' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '30px' }}>
              {/* Sessions Index List */}
              <div className="glass-card animate-fade-in" style={{ padding: '25px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <h3 className="font-serif" style={{ fontSize: '1.2rem', margin: '0 0 10px 0', color: 'var(--text-light)' }}>Active Seeker Sessions</h3>

                {chatSessions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
                    <MessageSquare size={36} style={{ margin: '0 auto 10px', opacity: 0.3 }} />
                    <p>No chat logs recorded.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '450px', overflowY: 'auto', paddingRight: '5px' }}>
                    {chatSessions.map((c) => (
                      <div
                        key={c.session_id}
                        onClick={() => handleInspectChat(c.session_id)}
                        style={{
                          border: selectedChatSession === c.session_id ? '1px solid var(--primary-gold)' : '1px solid rgba(181, 137, 37, 0.1)',
                          background: selectedChatSession === c.session_id ? 'rgba(181, 137, 37, 0.05)' : 'rgba(255, 255, 255, 0.3)',
                          borderRadius: '8px',
                          padding: '12px 15px',
                          cursor: 'pointer',
                          transition: 'var(--transition-fast)'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ fontSize: '0.75rem', fontFamily: 'monospace', fontWeight: 'bold', color: 'var(--text-light)' }}>
                              {c.session_id.substring(0, 15)}...
                            </span>
                            <span style={{ fontSize: '0.65rem', color: c.session_id.includes('_reg_') ? 'var(--primary-gold)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: c.session_id.includes('_reg_') ? '600' : '400' }}>
                              {c.session_id.includes('_reg_') ? 'Registered User' : 'Guest'}
                            </span>
                          </div>
                          <span style={{ fontSize: '0.7rem', color: 'var(--primary-gold)', fontWeight: 'bold', background: 'rgba(181,137,37,0.1)', padding: '2px 6px', borderRadius: '10px' }}>
                            {c.message_count} messages
                          </span>
                        </div>
                        <p style={{ margin: '0 0 8px 0', fontSize: '0.75rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.last_message}
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                            {new Date(c.last_timestamp).toLocaleString()}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteChatSession(c.session_id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--accent-terracotta)',
                              cursor: 'pointer',
                              padding: '2px'
                            }}
                            title="Clear History"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Chat transcripts detail display */}
              <div className="glass-card animate-fade-in" style={{ padding: '25px', display: 'flex', flexDirection: 'column', height: '515px' }}>
                <h3 className="font-serif" style={{ fontSize: '1.2rem', margin: '0 0 15px 0', color: 'var(--text-light)', borderBottom: '1px solid rgba(181,137,37,0.1)', paddingBottom: '10px' }}>
                  Transcript Inspection
                </h3>

                {!selectedChatSession ? (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', textAlign: 'center' }}>
                    <Shield size={40} style={{ opacity: 0.2, marginBottom: '15px' }} />
                    <p>Select a seeker conversation session from the left to view the interactive audit trail.</p>
                  </div>
                ) : (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '90%' }}>
                    {/* Chat Bubble Scroll Frame */}
                    <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '15px', paddingRight: '5px', marginBottom: '15px' }}>
                      {selectedChatHistory.map((m) => (
                        <div
                          key={m.id}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: m.sender === 'user' ? 'flex-end' : 'flex-start'
                          }}
                        >
                          <div
                            style={{
                              maxWidth: '85%',
                              padding: '12px 16px',
                              borderRadius: m.sender === 'user' ? '18px 18px 2px 18px' : '18px 18px 18px 2px',
                              backgroundColor: m.sender === 'user' ? 'rgba(212, 118, 55, 0.08)' : '#fff',
                              border: m.sender === 'user' ? '1px solid rgba(212, 118, 55, 0.2)' : '1px solid rgba(181, 137, 37, 0.15)',
                              color: 'var(--text-light)',
                              fontSize: '0.85rem',
                              lineHeight: '1.4'
                            }}
                          >
                            {m.text}
                          </div>
                          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '4px', padding: '0 4px' }}>
                            {m.sender === 'user' ? 'Seeker Inquiry' : 'Mystic Routes Guide'} • {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(181,137,37,0.08)', paddingTop: '12px' }}>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>Session: {selectedChatSession}</span>
                      <button
                        onClick={() => {
                          setSelectedChatSession(null);
                          setSelectedChatHistory([]);
                        }}
                        className="btn-outline"
                        style={{ padding: '4px 10px', borderRadius: '4px', fontSize: '0.75rem' }}
                      >
                        Close View
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* TAB 4: STORIES */}
          {activeTab === 'stories' && (
            <div className="glass-card animate-fade-in" style={{ padding: '30px' }}>
              {stories.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '50px 0', color: 'var(--text-muted)' }}>
                  <BookOpen size={48} style={{ margin: '0 auto 15px', opacity: 0.3 }} />
                  <p>No seeker stories found in the database.</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.15)', color: 'var(--text-muted)' }}>
                        <th style={{ padding: '12px 10px' }}>Seeker Details</th>
                        <th style={{ padding: '12px 10px' }}>Spiritual Journey</th>
                        <th style={{ padding: '12px 10px' }}>Rating</th>
                        <th style={{ padding: '12px 10px' }}>Contact Info</th>
                        <th style={{ padding: '12px 10px' }}>Status</th>
                        <th style={{ padding: '12px 10px' }}>Created At</th>
                        <th style={{ padding: '12px 10px', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stories.map((s) => (
                        <tr key={s.id} style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.05)', color: 'var(--text-light)' }}>
                          <td style={{ padding: '14px 10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              {s.image ? (
                                <img src={s.image} alt={s.author} style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--primary-gold-border)' }} />
                              ) : (
                                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: s.avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dark)', fontSize: '0.75rem', fontWeight: 'bold' }}>{s.initials}</div>
                              )}
                              <div>
                                <div style={{ fontWeight: '600' }}>{s.author}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{s.role} • {s.origin}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '14px 10px', fontWeight: '500', color: 'var(--primary-saffron)' }}>{s.journey}</td>
                          <td style={{ padding: '14px 10px' }}>
                            <div style={{ display: 'flex', gap: '3px' }}>
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  size={12}
                                  fill={s.rating && star <= s.rating ? 'var(--primary-gold)' : 'transparent'}
                                  stroke={s.rating && star <= s.rating ? 'var(--primary-gold)' : 'rgba(181, 137, 37, 0.3)'}
                                  strokeWidth={1.5}
                                />
                              ))}
                              {!s.rating && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>—</span>}
                            </div>
                          </td>
                          <td style={{ padding: '14px 10px', fontFamily: 'monospace' }}>{s.contact}</td>
                          <td style={{ padding: '14px 10px' }}>
                            <span
                              style={{
                                padding: '3px 8px',
                                borderRadius: '10px',
                                fontSize: '0.7rem',
                                fontWeight: '700',
                                backgroundColor: s.approved ? 'rgba(61, 90, 69, 0.1)' : 'rgba(194, 94, 26, 0.1)',
                                color: s.approved ? 'var(--accent-green)' : 'var(--primary-saffron)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                              }}
                            >
                              {s.approved ? 'Approved' : 'Pending'}
                            </span>
                          </td>
                          <td style={{ padding: '14px 10px' }}>{new Date(s.created_at).toLocaleDateString()}</td>
                          <td style={{ padding: '14px 10px', textAlign: 'right' }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                              {!s.approved && (
                                <button
                                  onClick={() => handleApproveStory(s.id)}
                                  className="btn-outline"
                                  style={{ padding: '4px 8px', borderRadius: '4px', borderColor: 'var(--accent-green)', color: 'var(--accent-green)', display: 'flex', alignItems: 'center' }}
                                  title="Approve Story"
                                >
                                  <Check size={14} />
                                </button>
                              )}
                              <button
                                onClick={() => setSelectedStory(s)}
                                className="btn-outline"
                                style={{ padding: '4px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center' }}
                                title="Inspect Story"
                              >
                                <Eye size={14} />
                              </button>
                              <button
                                onClick={() => handleDeleteStory(s.id)}
                                className="btn-outline"
                                style={{ padding: '4px 8px', borderRadius: '4px', borderColor: 'var(--accent-terracotta)', color: 'var(--accent-terracotta)' }}
                                title="Delete Story"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}



          {/* TAB 5: REGISTERED USERS */}
          {activeTab === 'users' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
              {/* ADMINS SECTION */}
              <div className="glass-card animate-fade-in" style={{ padding: '30px' }}>
                <h3 className="font-serif text-gold" style={{ fontSize: '1.4rem', marginBottom: '20px' }}>Administrators</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.15)', color: 'var(--text-muted)' }}>
                        <th style={{ padding: '12px 10px' }}>Full Name</th>
                        <th style={{ padding: '12px 10px' }}>Email Address</th>
                        <th style={{ padding: '12px 10px' }}>Phone Number</th>
                        <th style={{ padding: '12px 10px' }}>Registration Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.filter(u => u.role === 'admin').map((u) => (
                        <tr key={u.id} style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.05)', color: 'var(--text-light)' }}>
                          <td style={{ padding: '14px 10px', fontWeight: '500' }}>{u.name || 'Admin'}</td>
                          <td style={{ padding: '14px 10px' }}>{u.email}</td>
                          <td style={{ padding: '14px 10px' }}>{u.phone || 'N/A'}</td>
                          <td style={{ padding: '14px 10px' }}>{new Date(u.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* REGULAR USERS SECTION */}
              <div className="glass-card animate-fade-in" style={{ padding: '30px' }}>
                <h3 className="font-serif text-gold" style={{ fontSize: '1.4rem', marginBottom: '20px' }}>Registered Seekers</h3>
                {users.filter(u => u.role !== 'admin').length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '50px 0', color: 'var(--text-muted)' }}>
                    <ShieldCheck size={48} style={{ margin: '0 auto 15px', opacity: 0.3 }} />
                    <p>No regular user accounts found.</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.15)', color: 'var(--text-muted)' }}>
                          <th style={{ padding: '12px 10px' }}>Full Name</th>
                          <th style={{ padding: '12px 10px' }}>Email Address</th>
                          <th style={{ padding: '12px 10px' }}>Phone Number</th>
                          <th style={{ padding: '12px 10px' }}>Registration Date</th>
                          <th style={{ padding: '12px 10px', textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.filter(u => u.role !== 'admin').map((u) => (
                          <tr key={u.id} style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.05)', color: 'var(--text-light)' }}>
                            <td style={{ padding: '14px 10px', fontWeight: '500' }}>{u.name || 'Anonymous Seeker'}</td>
                            <td style={{ padding: '14px 10px' }}>{u.email}</td>
                            <td style={{ padding: '14px 10px' }}>{u.phone || 'N/A'}</td>
                            <td style={{ padding: '14px 10px' }}>{new Date(u.created_at).toLocaleString()}</td>
                            <td style={{ padding: '14px 10px', textAlign: 'right' }}>
                              <button
                                onClick={() => handleDeleteUser(u.id)}
                                className="btn-outline"
                                style={{ padding: '4px 8px', borderRadius: '4px', borderColor: 'var(--accent-terracotta)', color: 'var(--accent-terracotta)' }}
                                title="Delete User Account"
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 7: SANCTUARIES */}
          {activeTab === 'sanctuaries' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
              <div className="glass-card animate-fade-in" style={{ padding: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 className="font-serif text-gold" style={{ fontSize: '1.4rem' }}>Sacred Sanctuaries Management</h3>
                  <button
                    onClick={() => setShowAddSanctuaryModal(!showAddSanctuaryModal)}
                    className="btn-gold"
                    style={{ padding: '10px 20px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    {showAddSanctuaryModal ? <X size={18} /> : <Plus size={18} />}
                    {showAddSanctuaryModal ? 'Cancel' : 'Add Sanctuary'}
                  </button>
                </div>

                {sanctuaries.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '50px 0', color: 'var(--text-muted)' }}>
                    <Compass size={48} style={{ margin: '0 auto 15px', opacity: 0.3 }} />
                    <p>No sanctuaries configured in the database.</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.15)', color: 'var(--text-muted)' }}>
                          <th style={{ padding: '12px 10px' }}>Image</th>
                          <th style={{ padding: '12px 10px' }}>Title</th>
                          <th style={{ padding: '12px 10px' }}>State</th>
                          <th style={{ padding: '12px 10px' }}>Tagline</th>
                          <th style={{ padding: '12px 10px', textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sanctuaries.map((s) => (
                          <tr key={s.id} style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.05)', color: 'var(--text-light)' }}>
                            <td style={{ padding: '14px 10px' }}>
                              <img src={s.image} alt={s.title} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} />
                            </td>
                            <td style={{ padding: '14px 10px', fontWeight: 'bold' }}>{s.title}</td>
                            <td style={{ padding: '14px 10px' }}>{s.state}</td>
                            <td style={{ padding: '14px 10px' }}>{s.tagline}</td>
                            <td style={{ padding: '14px 10px', textAlign: 'right' }}>
                              <button
                                onClick={() => handleDeleteSanctuary(s.id)}
                                className="btn-outline"
                                style={{ padding: '4px 8px', borderRadius: '4px', borderColor: 'var(--accent-terracotta)', color: 'var(--accent-terracotta)' }}
                                title="Delete Sanctuary"
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 8: EXPERIENCES */}
          {activeTab === 'experiences' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
              <div className="glass-card animate-fade-in" style={{ padding: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 className="font-serif text-gold" style={{ fontSize: '1.4rem' }}>Spiritual Experiences Management</h3>
                  <button
                    onClick={() => setShowAddExperienceModal(!showAddExperienceModal)}
                    className="btn-gold"
                    style={{ padding: '10px 20px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    {showAddExperienceModal ? <X size={18} /> : <Plus size={18} />}
                    {showAddExperienceModal ? 'Cancel' : 'Add Experience'}
                  </button>
                </div>

                {experiences.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '50px 0', color: 'var(--text-muted)' }}>
                    <Compass size={48} style={{ margin: '0 auto 15px', opacity: 0.3 }} />
                    <p>No experiences configured in the database.</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.15)', color: 'var(--text-muted)' }}>
                          <th style={{ padding: '12px 10px' }}>Image</th>
                          <th style={{ padding: '12px 10px' }}>Title</th>
                          <th style={{ padding: '12px 10px' }}>Icon</th>
                          <th style={{ padding: '12px 10px' }}>Philosophy/Tagline</th>
                          <th style={{ padding: '12px 10px', textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {experiences.map((exp) => (
                          <tr key={exp.id} style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.05)', color: 'var(--text-light)' }}>
                            <td style={{ padding: '14px 10px' }}>
                              <img src={exp.image} alt={exp.title} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} />
                            </td>
                            <td style={{ padding: '14px 10px', fontWeight: 'bold' }}>{exp.title}</td>
                            <td style={{ padding: '14px 10px' }}>{exp.icon}</td>
                            <td style={{ padding: '14px 10px' }}>{exp.philosophy}</td>
                            <td style={{ padding: '14px 10px', textAlign: 'right' }}>
                              <button
                                onClick={() => handleDeleteExperience(exp.id)}
                                className="btn-outline"
                                style={{ padding: '4px 8px', borderRadius: '4px', borderColor: 'var(--accent-terracotta)', color: 'var(--accent-terracotta)' }}
                                title="Delete Experience"
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 9: SUBSCRIBERS */}
          {activeTab === 'subscribers' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
              <div className="glass-card animate-fade-in" style={{ padding: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                  <div>
                    <h3 className="font-serif text-gold" style={{ fontSize: '1.4rem', margin: 0 }}>Newsletter Subscribers</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '5px' }}>
                      Manage the mailing list for regular updates and announcements.
                    </p>
                  </div>
                </div>

                {subscribers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '50px 0', color: 'var(--text-muted)' }}>
                    <Mail size={48} style={{ margin: '0 auto 15px', opacity: 0.3 }} />
                    <p>No newsletter subscribers found.</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto', background: '#ffffff', borderRadius: '8px', padding: '15px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.15)', color: 'var(--primary-gold)' }}>
                          <th style={{ padding: '12px 10px' }}>ID</th>
                          <th style={{ padding: '12px 10px' }}>Email Address</th>
                          <th style={{ padding: '12px 10px' }}>Account Status</th>
                          <th style={{ padding: '12px 10px' }}>Subscribed On</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscribers.map((sub) => {
                          const isRegistered = users.some(u => u.email.toLowerCase() === sub.email.toLowerCase());
                          return (
                            <tr key={sub.id} style={{ borderBottom: '1px solid rgba(181, 137, 37, 0.05)', color: 'var(--text-light)', transition: 'background-color 0.2s ease' }}>
                              <td style={{ padding: '14px 10px', color: 'var(--text-muted)' }}>#{sub.id}</td>
                              <td style={{ padding: '14px 10px', fontWeight: 'bold' }}>{sub.email}</td>
                              <td style={{ padding: '14px 10px' }}>
                                <span
                                  style={{
                                    padding: '3px 8px',
                                    borderRadius: '10px',
                                    fontSize: '0.7rem',
                                    fontWeight: '700',
                                    backgroundColor: isRegistered ? 'rgba(61, 90, 69, 0.1)' : 'rgba(181, 137, 37, 0.1)',
                                    color: isRegistered ? 'var(--accent-green)' : 'var(--primary-gold)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                  }}
                                >
                                  {isRegistered ? 'Registered User' : 'Guest'}
                                </span>
                              </td>
                              <td style={{ padding: '14px 10px', color: 'var(--text-muted)' }}>{new Date(sub.created_at).toLocaleString()}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>




      {/* RENDER BOOKING DETAILS MODAL */}
      {selectedBooking && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(7, 5, 4, 0.4)',
            backdropFilter: 'blur(4px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div
            className="glass-card animate-fade-in"
            data-lenis-prevent
            style={{
              width: '100%',
              maxWidth: '550px',
              padding: '30px 25px',
              position: 'relative'
            }}
          >
            <button
              onClick={() => setSelectedBooking(null)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer'
              }}
            >
              <X size={20} />
            </button>

            <span style={{ fontSize: '0.75rem', letterSpacing: '0.1em', fontWeight: 'bold', color: 'var(--primary-gold)' }}>BOOKING REGISTRATION</span>
            <h3 className="font-serif" style={{ fontSize: '1.6rem', color: 'var(--text-light)', margin: '5px 0 20px 0' }}>Ref: {selectedBooking.booking_ref}</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', fontSize: '0.85rem', marginBottom: '20px' }}>
              <div>
                <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.75rem' }}>Seeker Name</span>
                <strong style={{ color: 'var(--text-light)' }}>{selectedBooking.name}</strong>
              </div>
              <div>
                <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.75rem' }}>Email Address</span>
                <strong style={{ color: 'var(--text-light)' }}>{selectedBooking.email}</strong>
              </div>
              <div>
                <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.75rem' }}>Spiritual Package</span>
                <strong style={{ color: 'var(--text-light)' }}>{getPackageNameText(selectedBooking.package_name)}</strong>
              </div>
              <div>
                <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.75rem' }}>Comfort Tier</span>
                <strong style={{ color: 'var(--text-light)' }}>{getComfortTierText(selectedBooking.comfort)}</strong>
              </div>
              <div>
                <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.75rem' }}>Duration & Guests</span>
                <strong style={{ color: 'var(--text-light)' }}>{selectedBooking.duration} Days — {selectedBooking.guests} Seeker(s)</strong>
              </div>
              <div>
                <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.75rem' }}>Start Date</span>
                <strong style={{ color: 'var(--text-light)' }}>{selectedBooking.start_date}</strong>
              </div>
              {selectedBooking.phone && (
                <div>
                  <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.75rem' }}>Phone Number</span>
                  <strong style={{ color: 'var(--text-light)' }}>{selectedBooking.phone}</strong>
                </div>
              )}
            </div>

            <div style={{ borderTop: '1px solid rgba(181,137,37,0.1)', paddingTop: '15px', marginBottom: '20px' }}>
              <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: '5px' }}>Spiritual Intention & Focus</span>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-light)', fontStyle: 'italic', lineHeight: '1.4' }}>
                "{selectedBooking.intention || 'No custom intention stated.'}"
              </p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(181,137,37,0.1)', paddingTop: '15px' }}>
              <div>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Total Invoice Rate:</span>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--primary-gold)' }}>${selectedBooking.price}</div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => handleDeleteBooking(selectedBooking.id)}
                  className="btn-outline"
                  style={{ padding: '8px 16px', borderRadius: '4px', borderColor: 'var(--accent-terracotta)', color: 'var(--accent-terracotta)', fontSize: '0.8rem' }}
                >
                  Delete Booking
                </button>
                <button
                  onClick={() => setSelectedBooking(null)}
                  className="btn-gold"
                  style={{ padding: '8px 16px', borderRadius: '4px', fontSize: '0.8rem' }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RENDER STORY DETAILS MODAL */}
      {selectedStory && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(7, 5, 4, 0.4)',
            backdropFilter: 'blur(4px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div
            className="glass-card animate-fade-in"
            data-lenis-prevent
            style={{
              width: '100%',
              maxWidth: '550px',
              padding: '30px 25px',
              position: 'relative'
            }}
          >
            <button
              onClick={() => setSelectedStory(null)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer'
              }}
            >
              <X size={20} />
            </button>

            <span style={{ fontSize: '0.75rem', letterSpacing: '0.1em', fontWeight: 'bold', color: 'var(--primary-gold)' }}>SEEKER EXPERIENCE DETAIL</span>
            <h3 className="font-serif" style={{ fontSize: '1.6rem', color: 'var(--text-light)', margin: '5px 0 20px 0' }}>By: {selectedStory.author}</h3>

            <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid rgba(181, 137, 37, 0.1)', paddingBottom: '15px' }}>
              {selectedStory.image ? (
                <img src={selectedStory.image} alt={selectedStory.author} style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--primary-gold)' }} />
              ) : (
                <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: selectedStory.avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dark)', fontSize: '2rem', fontWeight: 'bold' }}>{selectedStory.initials}</div>
              )}
              <div>
                <h4 style={{ margin: 0, color: 'var(--text-light)' }}>{selectedStory.author}</h4>
                <p style={{ margin: '2px 0 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>{selectedStory.role} from {selectedStory.origin}</p>
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px', alignItems: 'center' }}>
                  <span style={{ display: 'inline-block', fontSize: '0.75rem', color: 'var(--primary-saffron)', backgroundColor: 'rgba(194, 94, 26, 0.08)', padding: '2px 8px', borderRadius: '10px' }}>{selectedStory.journey}</span>
                  <span
                    style={{
                      display: 'inline-block',
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      backgroundColor: selectedStory.approved ? 'rgba(61, 90, 69, 0.1)' : 'rgba(194, 94, 26, 0.1)',
                      color: selectedStory.approved ? 'var(--accent-green)' : 'var(--primary-saffron)',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {selectedStory.approved ? 'Approved' : 'Pending'}
                  </span>
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px', fontSize: '0.85rem', marginBottom: '20px' }}>
              <div>
                <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.75rem' }}>Contact Info</span>
                <strong style={{ color: 'var(--text-light)' }}>{selectedStory.contact}</strong>
              </div>
              {selectedStory.rating && (
                <div>
                  <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: '5px' }}>Experience Rating</span>
                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        size={18}
                        fill={star <= selectedStory.rating ? 'var(--primary-gold)' : 'transparent'}
                        stroke={star <= selectedStory.rating ? 'var(--primary-gold)' : 'rgba(181, 137, 37, 0.3)'}
                        strokeWidth={1.5}
                      />
                    ))}
                    <span style={{ marginLeft: '5px', fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: '600' }}>{selectedStory.rating}/5</span>
                  </div>
                </div>
              )}
              <div>
                <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: '5px' }}>Spiritual Transformation Experience</span>
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-light)', fontStyle: 'italic', lineHeight: '1.5', background: 'rgba(255,255,255,0.4)', padding: '15px', borderRadius: '8px', border: '1px solid rgba(181,137,37,0.1)' }}>
                  "{selectedStory.quote}"
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid rgba(181,137,37,0.1)', paddingTop: '15px' }}>
              {!selectedStory.approved && (
                <button
                  onClick={() => handleApproveStory(selectedStory.id)}
                  className="btn-outline"
                  style={{ padding: '8px 16px', borderRadius: '4px', borderColor: 'var(--accent-green)', color: 'var(--accent-green)', fontSize: '0.8rem' }}
                >
                  Approve Story
                </button>
              )}
              <button
                onClick={() => handleDeleteStory(selectedStory.id)}
                className="btn-outline"
                style={{ padding: '8px 16px', borderRadius: '4px', borderColor: 'var(--accent-terracotta)', color: 'var(--accent-terracotta)', fontSize: '0.8rem' }}
              >
                Delete Story
              </button>
              <button
                onClick={() => setSelectedStory(null)}
                className="btn-gold"
                style={{ padding: '8px 16px', borderRadius: '4px', fontSize: '0.8rem' }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD JOURNEY MODAL */}
      {showAddJourneyModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(7, 5, 4, 0.4)',
            backdropFilter: 'blur(4px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div
            className="glass-card animate-fade-in"
            data-lenis-prevent
            style={{
              width: '100%',
              maxWidth: '650px',
              padding: '30px 25px',
              position: 'relative',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <button
              onClick={() => {
                setShowAddJourneyModal(false);
                setDays([{ title: '', detail: '' }]);
              }}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                zIndex: 10
              }}
            >
              <X size={20} />
            </button>

            <span style={{ fontSize: '0.75rem', letterSpacing: '0.15em', fontWeight: 'bold', color: 'var(--primary-gold)', textTransform: 'uppercase' }}>DESIGN NEW ITINERARY</span>
            <h3 className="font-serif" style={{ fontSize: '1.6rem', color: 'var(--text-light)', margin: '5px 0 20px 0' }}>Add Spiritual Journey</h3>

            <form onSubmit={handleCreateJourneySubmit} data-lenis-prevent style={{ display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'auto', paddingRight: '5px', flex: 1 }}>

              {/* Row 1: Title & Focus */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '5px', fontWeight: '600' }}>Journey Title</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g., Sacred Himalayan Hatha Yoga Path"
                    value={newJourney.title}
                    onChange={(e) => setNewJourney(prev => ({ ...prev, title: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid rgba(181, 137, 37, 0.25)',
                      background: 'rgba(255,255,255,0.75)',
                      outline: 'none',
                      fontSize: '0.85rem'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '5px', fontWeight: '600' }}>Spiritual Focus</label>
                  <select
                    value={newJourney.focus}
                    onChange={(e) => setNewJourney(prev => ({ ...prev, focus: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid rgba(181, 137, 37, 0.25)',
                      background: 'rgba(255,255,255,0.75)',
                      outline: 'none',
                      fontSize: '0.85rem'
                    }}
                  >
                    <option value="Yoga">Hatha & Ashtanga Yoga</option>
                    <option value="Meditation">Vipassana Meditation</option>
                    <option value="Ayurveda">Ayurvedic Healing</option>
                    <option value="Pilgrimage">Sacred Geometry Pilgrimage</option>
                    <option value="Contemplative">Silent Reflection</option>
                    <option value="Devotion">Ganga Aarti & Ritual Devotion</option>
                    <option value="Peace">Emotional Healing & Peace</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Comfort, Region */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '5px', fontWeight: '600' }}>Comfort Tier</label>
                  <select
                    value={newJourney.comfort}
                    onChange={(e) => setNewJourney(prev => ({ ...prev, comfort: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid rgba(181, 137, 37, 0.25)',
                      background: 'rgba(255,255,255,0.75)',
                      outline: 'none',
                      fontSize: '0.85rem'
                    }}
                  >
                    <option value="ashram">Ashram Simplicity</option>
                    <option value="heritage">Heritage Comfort</option>
                    <option value="royal">Royal Luxury (Palace)</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '5px', fontWeight: '600' }}>Region</label>
                  <select
                    value={newJourney.region}
                    onChange={(e) => setNewJourney(prev => ({ ...prev, region: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid rgba(181, 137, 37, 0.25)',
                      background: 'rgba(255,255,255,0.75)',
                      outline: 'none',
                      fontSize: '0.85rem'
                    }}
                  >
                    <option value="North India">North India (Himalayas/Ganges)</option>
                    <option value="South India">South India (Tamil Nadu/Kerala)</option>
                    <option value="East India">East India (Bodhgaya/Assam)</option>
                    <option value="West India">West India (Gujarat/Maharashtra)</option>
                  </select>
                </div>
              </div>

              {/* Row 3: Summary */}
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '5px', fontWeight: '600' }}>Journey Summary</label>
                <textarea
                  required
                  placeholder="Provide an evocative description of this spiritual tour, including highlights, sensory details, and general goals of the journey..."
                  rows={3}
                  value={newJourney.summary}
                  onChange={(e) => setNewJourney(prev => ({ ...prev, summary: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid rgba(181, 137, 37, 0.25)',
                    background: 'rgba(255,255,255,0.75)',
                    outline: 'none',
                    fontSize: '0.85rem',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* Day-by-Day Itinerary Planner Section */}
              <div style={{ borderTop: '1px solid rgba(181, 137, 37, 0.15)', paddingTop: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4 className="font-serif" style={{ fontSize: '1.1rem', color: 'var(--text-light)', margin: 0 }}>Day-by-Day Itinerary</h4>
                  <button
                    type="button"
                    onClick={handleAddDay}
                    className="btn-outline"
                    style={{ padding: '6px 12px', borderRadius: '20px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <Plus size={12} /> Add Itinerary Day
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {days.map((day, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '15px',
                        borderRadius: '8px',
                        border: '1px solid rgba(181, 137, 37, 0.12)',
                        background: 'rgba(255, 255, 255, 0.15)',
                        position: 'relative'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'var(--primary-gold)' }}>Day {index + 1}</span>
                        {days.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveDay(index)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--accent-terracotta)',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '2px'
                            }}
                          >
                            <Trash2 size={12} /> Remove
                          </button>
                        )}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <input
                          type="text"
                          required
                          placeholder="Day Theme (e.g., Sunset Meditation by the Ganges)"
                          value={day.title}
                          onChange={(e) => handleDayChange(index, 'title', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid rgba(181, 137, 37, 0.15)',
                            background: 'rgba(255,255,255,0.8)',
                            fontSize: '0.8rem',
                            outline: 'none'
                          }}
                        />
                        <textarea
                          required
                          placeholder="Itinerary Details (describe the spiritual activities, ashram visits, prayers, hikes, or dietary details for this day)..."
                          rows={2}
                          value={day.detail}
                          onChange={(e) => handleDayChange(index, 'detail', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid rgba(181, 137, 37, 0.15)',
                            background: 'rgba(255,255,255,0.8)',
                            fontSize: '0.8rem',
                            outline: 'none',
                            resize: 'vertical',
                            fontFamily: 'inherit'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid rgba(181, 137, 37, 0.15)', paddingTop: '15px', marginTop: '10px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddJourneyModal(false);
                    setDays([{ title: '', detail: '' }]);
                  }}
                  className="btn-outline"
                  style={{ padding: '10px 20px', borderRadius: '4px', fontSize: '0.8rem' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-gold"
                  style={{ padding: '10px 20px', borderRadius: '4px', fontSize: '0.8rem' }}
                >
                  Publish Journey
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      
      {showAddSanctuaryModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(7, 5, 4, 0.5)', backdropFilter: 'blur(10px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div className="animate-fade-in" style={{ width: '100%', maxWidth: '600px', maxHeight: '90vh', borderRadius: '12px', padding: '0', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', margin: 'auto', backgroundColor: '#ffffff' }}>
            <div style={{ padding: '20px 25px', borderBottom: '1px solid rgba(181, 137, 37, 0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'rgba(10, 8, 6, 0.2)' }}>
              <h3 className="font-serif text-gold" style={{ fontSize: '1.2rem', margin: 0 }}>Add New Sanctuary</h3>
              <button type="button" onClick={() => setShowAddSanctuaryModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', padding: '4px' }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateSanctuarySubmit} data-lenis-prevent style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto' }}>
<div style={{ padding: '25px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', gap: '15px' }}>
                      
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Title</label>
                        <input
                          type="text"
                          placeholder="e.g., Varanasi"
                          value={newSanctuary.title}
                          onChange={(e) => setNewSanctuary({ ...newSanctuary, title: e.target.value })}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none' }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>State</label>
                        <input
                          type="text"
                          placeholder="e.g., Uttar Pradesh"
                          value={newSanctuary.state}
                          onChange={(e) => setNewSanctuary({ ...newSanctuary, state: e.target.value })}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none' }}
                        />
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Tagline (Min 5 chars)</label>
                      <input
                        type="text"
                        placeholder="e.g., The Eternal City of Light..."
                        value={newSanctuary.tagline}
                        onChange={(e) => setNewSanctuary({ ...newSanctuary, tagline: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Upload Image</label>
                      <div
                        style={{
                          border: '1px dashed rgba(181, 137, 37, 0.3)',
                          borderRadius: '6px',
                          padding: '16px',
                          textAlign: 'center',
                          background: 'rgba(255, 255, 255, 0.7)',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: '120px',
                          position: 'relative'
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const file = e.dataTransfer.files[0];
                          if (file) {
                            if (file.size > 2 * 1024 * 1024) {
                              showToast("Image size must be less than 2MB.", "error");
                              return;
                            }
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setNewSanctuary(prev => ({ ...prev, image: reader.result }));
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      >
                        {newSanctuary.image ? (
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <img
                              src={newSanctuary.image}
                              alt="Sanctuary preview"
                              style={{
                                maxWidth: '100%',
                                maxHeight: '150px',
                                objectFit: 'contain',
                                borderRadius: '4px',
                                border: '1px solid rgba(181, 137, 37, 0.15)'
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => setNewSanctuary({ ...newSanctuary, image: '' })}
                              style={{
                                position: 'absolute',
                                top: '-6px',
                                right: '-6px',
                                background: 'var(--accent-terracotta)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                padding: 0,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }}
                              title="Remove Image"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <input
                              type="file"
                              accept="image/*"
                              id="sanctuary-image-upload"
                              onChange={handleSanctuaryImageChange}
                              style={{ display: 'none' }}
                            />
                            <label
                              htmlFor="sanctuary-image-upload"
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '6px',
                                cursor: 'pointer',
                                width: '100%',
                                padding: '10px'
                              }}
                            >
                              <ImageIcon size={24} style={{ color: 'var(--primary-gold)', opacity: 0.8 }} />
                              <span style={{ fontSize: '0.8rem', color: 'var(--text-light)', fontWeight: '600' }}>
                                Click to select image or drag here
                              </span>
                              <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                                Max file size: 2MB (PNG, JPG, WEBP)
                              </span>
                            </label>
                          </>
                        )}
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Description (Min 10 chars)</label>
                      <textarea
                        placeholder="Detailed history and description..."
                        rows={3}
                        value={newSanctuary.description}
                        onChange={(e) => setNewSanctuary({ ...newSanctuary, description: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none', resize: 'vertical' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Spiritual Essence (Min 10 chars)</label>
                      <textarea
                        placeholder="Describe the spiritual feeling or essence..."
                        rows={2}
                        value={newSanctuary.spiritualEssence}
                        onChange={(e) => setNewSanctuary({ ...newSanctuary, spiritualEssence: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none', resize: 'vertical' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Activities (One per line)</label>
                      <textarea
                        placeholder="Witness the hypnotic Ganga Aarti...&#10;Take a silent sunrise boat ride..."
                        rows={3}
                        value={newSanctuary.activities}
                        onChange={(e) => setNewSanctuary({ ...newSanctuary, activities: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none', resize: 'vertical' }}
                      />
                    </div>

                    <div style={{ display: 'flex', gap: '15px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Best Time to Visit</label>
                        <input
                          type="text"
                          placeholder="e.g., October to March"
                          value={newSanctuary.bestTime}
                          onChange={(e) => setNewSanctuary({ ...newSanctuary, bestTime: e.target.value })}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none' }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Signature Experience (Min 10 chars)</label>
                        <input
                          type="text"
                          placeholder="e.g., Sunrise Boat Meditation: Watch..."
                          value={newSanctuary.signatureExperience}
                          onChange={(e) => setNewSanctuary({ ...newSanctuary, signatureExperience: e.target.value })}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none' }}
                        />
                      </div>
                    </div>

                    </div>
<div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '20px 25px', borderTop: '1px solid rgba(181, 137, 37, 0.15)', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
                      <button
                        type="button"
                        onClick={() => setShowAddSanctuaryModal(false)}
                        className="btn-outline"
                        style={{ padding: '10px 20px', borderRadius: '4px', fontSize: '0.8rem' }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn-gold"
                        style={{ padding: '10px 20px', borderRadius: '4px', fontSize: '0.8rem' }}
                      >
                        Save Sanctuary
                      </button>
                    </div>
                  </form>
          </div>
        </div>
      )}


      {showAddExperienceModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(7, 5, 4, 0.5)', backdropFilter: 'blur(10px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div className="animate-fade-in" style={{ width: '100%', maxWidth: '600px', maxHeight: '90vh', borderRadius: '12px', padding: '0', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', margin: 'auto', backgroundColor: '#ffffff' }}>
            <div style={{ padding: '20px 25px', borderBottom: '1px solid rgba(181, 137, 37, 0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'rgba(10, 8, 6, 0.2)' }}>
              <h3 className="font-serif text-gold" style={{ fontSize: '1.2rem', margin: 0 }}>Add New Experience</h3>
              <button type="button" onClick={() => setShowAddExperienceModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', padding: '4px' }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateExperienceSubmit} data-lenis-prevent style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto' }}>
<div style={{ padding: '25px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', gap: '15px' }}>
                      
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Title</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g., Yoga & Breathwork"
                          value={newExperience.title}
                          onChange={(e) => setNewExperience({ ...newExperience, title: e.target.value })}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none' }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Icon</label>
                        <select
                          value={newExperience.icon}
                          onChange={(e) => setNewExperience({ ...newExperience, icon: e.target.value })}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', color: 'var(--text-light)', outline: 'none' }}
                        >
                          <option value="Sun">Sun (Yoga)</option>
                          <option value="Moon">Moon (Meditation)</option>
                          <option value="Eye">Eye (Temples)</option>
                          <option value="Feather">Feather (Churches/Shrines)</option>
                          <option value="Heart">Heart (Ayurveda)</option>
                          <option value="Compass">Compass (Yatra/Pilgrimage)</option>
                        </select>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '15px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Philosophy/Tagline (Min 10 chars)</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g., Yogas Chitta Vritti Nirodhah"
                          value={newExperience.philosophy}
                          onChange={(e) => setNewExperience({ ...newExperience, philosophy: e.target.value })}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none' }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Philosophy Translation</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g., Yoga is the cessation of..."
                          value={newExperience.philosophyTranslation}
                          onChange={(e) => setNewExperience({ ...newExperience, philosophyTranslation: e.target.value })}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none' }}
                        />
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Upload Image</label>
                      <div
                        style={{
                          border: '1px dashed rgba(181, 137, 37, 0.3)',
                          borderRadius: '6px',
                          padding: '16px',
                          textAlign: 'center',
                          background: 'rgba(255, 255, 255, 0.7)',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: '120px',
                          position: 'relative'
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const file = e.dataTransfer.files[0];
                          if (file) {
                            if (file.size > 2 * 1024 * 1024) {
                              showToast("Image size must be less than 2MB.", "error");
                              return;
                            }
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setNewExperience(prev => ({ ...prev, image: reader.result }));
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      >
                        {newExperience.image ? (
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <img
                              src={newExperience.image}
                              alt="Experience preview"
                              style={{
                                maxWidth: '100%',
                                maxHeight: '150px',
                                objectFit: 'contain',
                                borderRadius: '4px',
                                border: '1px solid rgba(181, 137, 37, 0.15)'
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => setNewExperience({ ...newExperience, image: '' })}
                              style={{
                                position: 'absolute',
                                top: '-6px',
                                right: '-6px',
                                background: 'var(--accent-terracotta)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                padding: 0,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }}
                              title="Remove Image"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <input
                              type="file"
                              accept="image/*"
                              id="experience-image-upload"
                              onChange={handleExperienceImageChange}
                              style={{ display: 'none' }}
                            />
                            <label
                              htmlFor="experience-image-upload"
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '6px',
                                cursor: 'pointer',
                                width: '100%',
                                padding: '10px'
                              }}
                            >
                              <ImageIcon size={24} style={{ color: 'var(--primary-gold)', opacity: 0.8 }} />
                              <span style={{ fontSize: '0.8rem', color: 'var(--text-light)', fontWeight: '600' }}>
                                Click to select image or drag here
                              </span>
                              <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                                Max file size: 2MB (PNG, JPG, WEBP)
                              </span>
                            </label>
                          </>
                        )}
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Description (Min 10 chars)</label>
                      <textarea
                        placeholder="Detailed description..."
                        rows={3}
                        required
                        value={newExperience.description}
                        onChange={(e) => setNewExperience({ ...newExperience, description: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none', resize: 'vertical' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Core Teachings/Pillars (One per line)</label>
                      <textarea
                        placeholder="Asana (Physical postures to release energetic blocks)...&#10;Pranayama (Conscious regulation of vital life force)..."
                        rows={3}
                        required
                        value={newExperience.teachings}
                        onChange={(e) => setNewExperience({ ...newExperience, teachings: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none', resize: 'vertical' }}
                      />
                    </div>

                    <div style={{ display: 'flex', gap: '15px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Signature Centers</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g., Parmarth Niketan (Rishikesh), Isha Yoga Center (Coimbatore)..."
                          value={newExperience.centers}
                          onChange={(e) => setNewExperience({ ...newExperience, centers: e.target.value })}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none' }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Recommended Duration</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g., Typically 7 to 28 days for full immersive programs"
                          value={newExperience.duration}
                          onChange={(e) => setNewExperience({ ...newExperience, duration: e.target.value })}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none' }}
                        />
                      </div>
                    </div>

                    </div>
<div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '20px 25px', borderTop: '1px solid rgba(181, 137, 37, 0.15)', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
                      <button
                        type="button"
                        onClick={() => setShowAddExperienceModal(false)}
                        className="btn-outline"
                        style={{ padding: '10px 20px', borderRadius: '4px', fontSize: '0.8rem' }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn-gold"
                        style={{ padding: '10px 20px', borderRadius: '4px', fontSize: '0.8rem' }}
                      >
                        Save Experience
                      </button>
                    </div>
                  </form>
          </div>
        </div>
      )}

      {/* CREATE RETREAT MODAL */}
      {showAddRetreatModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(7, 5, 4, 0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div
            className="glass-card animate-fade-in"
            style={{
              width: '100%',
              maxWidth: '650px',
              maxHeight: '90vh',
              padding: '0',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              margin: 'auto'
            }}
          >
            {/* Header */}
            <div style={{ padding: '20px 25px', borderBottom: '1px solid rgba(181, 137, 37, 0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'rgba(10, 8, 6, 0.2)' }}>
              <h3 className="font-serif text-gold" style={{ fontSize: '1.2rem', margin: 0 }}>Add New Retreat</h3>
              <button
                onClick={() => setShowAddRetreatModal(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', padding: '4px' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Form Content */}
            <form onSubmit={handleCreateRetreatSubmit} data-lenis-prevent style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <div style={{ padding: '25px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ display: 'flex', gap: '15px' }}>
                  <div style={{ flex: 2 }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Name</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g., Himalayan Hatha Yoga"
                      value={newRetreat.name}
                      onChange={(e) => setNewRetreat({ ...newRetreat, name: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Short Description (Min 10 chars)</label>
                  <input
                    type="text"
                    required
                    placeholder="Short summary for the list view..."
                    value={newRetreat.desc}
                    onChange={(e) => setNewRetreat({ ...newRetreat, desc: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Image Upload</label>
                  <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                    <input
                      type="file"
                      accept="image/*"
                      required={!newRetreat.image}
                      onChange={handleRetreatImageChange}
                      style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none' }}
                    />
                    {newRetreat.image && (
                      <div style={{ width: '40px', height: '40px', borderRadius: '4px', overflow: 'hidden', border: '1px solid var(--primary-gold-border)' }}>
                        <img src={newRetreat.image} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Full Description (Min 20 chars)</label>
                  <textarea
                    required
                    placeholder="Detailed description shown in the booking modal..."
                    rows={4}
                    value={newRetreat.fullDesc}
                    onChange={(e) => setNewRetreat({ ...newRetreat, fullDesc: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none', resize: 'vertical' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>Highlights (One per line)</label>
                  <textarea
                    required
                    placeholder="Daily Sunrise Hatha Yoga&#10;Pranayama & Breathwork Masterclasses"
                    rows={3}
                    value={newRetreat.highlights}
                    onChange={(e) => setNewRetreat({ ...newRetreat, highlights: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.2)', background: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', outline: 'none', resize: 'vertical' }}
                  />
                </div>
              </div>

              {/* Actions Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid rgba(181, 137, 37, 0.15)', padding: '15px 25px', backgroundColor: 'rgba(10, 8, 6, 0.2)' }}>
                <button
                  type="button"
                  onClick={() => setShowAddRetreatModal(false)}
                  className="btn-outline"
                  style={{ padding: '10px 20px', borderRadius: '4px', fontSize: '0.8rem' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-gold"
                  style={{ padding: '10px 20px', borderRadius: '4px', fontSize: '0.8rem' }}
                >
                  Add Retreat
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RENDER ADD SANCTUARY MODAL REMOVED - NOW INLINE */}

      <style>{`
        @media (max-width: 768px) {
          .admin-page { padding: 20px 0 !important; }
          .admin-page .glass-card { padding: 16px !important; }
          .admin-toast { top: auto !important; bottom: 16px !important; right: 16px !important; left: 16px !important; width: auto !important; }
          .admin-page [style*="display: flex"][style*="gap"]:not([style*="flex-wrap"]) {
            flex-wrap: wrap !important;
          }
          .admin-page [style*="flex: 1"] { flex: none !important; width: 100% !important; }
        }
      `}</style>
    </div>

  );
}

